import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import { Box, Typography } from "@mui/material";
import * as pdfjs from "pdfjs-dist/build/pdf";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface FilePreviewProps {
  fileUrl: string;
  fileType: string;
  isPreviewModal?: boolean;
  viewportHeight?: string;
  viewportWidth?: string;
  hideToolbar?: boolean;
}

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export default function FilePreview(props: FilePreviewProps) {
  const [_error, setError] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | ArrayBuffer>("");
  const [pdfObjectURL, setPDFObjectURL] = useState<string>("");
  const [txtObjectURL, setTxtObjectURL] = useState<string>("");
  const [markdownContent, setMarkdownContent] = useState<string>("");
  const [svgObjectURL, setSvgObjectURL] = useState<string>("");
  const [csvData, setCsvData] = useState<string[][]>([]);
  let isCancelled = false;

  const filesTypes = props.isPreviewModal
    ? ["doc", "docx", "xls", "xlsx", "sheets", "ppt", "pptx", "pdf", "txt", "md", "csv"]
    : ["doc", "docx", "xls", "xlsx", "sheets", "ppt", "pptx", "txt", "md"];

  async function loadPDF() {
    try {
      const loadingTask = pdfjs.getDocument(props.fileUrl);
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1 });

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({ canvasContext: context, viewport }).promise;

      const imageDataUrl = canvas.toDataURL("image/jpeg");
      if (!isCancelled) {
        setImagePreview(imageDataUrl);
        setError(false);
      }
    } catch (err) {
      if (!isCancelled) {
        setError(true);
      }
    }
  }

  useEffect(() => {
    setImagePreview("");
    setPDFObjectURL("");
    setTxtObjectURL("");
    setMarkdownContent("");
    setSvgObjectURL("");
    setCsvData([]);
    setError(false);

    if (props.fileType === "pdf") {
      if (props.isPreviewModal) {
        loadPDFIntoMemory();
      } else {
        loadPDF();
      }
    } else if (props.fileType === "txt") {
      loadTxtIntoMemory();
    } else if (props.fileType === "csv") {
      loadCSVIntoMemory();
    } else if (props.fileType === "md") {
      loadMarkdownIntoMemory();
    } else if (props.fileType === "svg") {
      loadSVGIntoMemory();
    }

    return () => {
      isCancelled = true;
    };
  }, [props.fileUrl, props.fileType]);

  function loadPDFIntoMemory() {
    fetch(props.fileUrl)
      .then((r) => {
        r.arrayBuffer().then((pdf) => {
          const pdfBlob = new Blob([new Uint8Array(pdf, 0)], {
            type: "application/pdf",
          });
          let objectURL = URL.createObjectURL(pdfBlob) + "#navpanes=0&view=fitH";
          if (props.hideToolbar) {
            objectURL += "&toolbar=0";
          }
          setPDFObjectURL(objectURL);
        });
      })
      .catch((_) => {
        setError(true);
      });
  }

  function loadTxtIntoMemory() {
    fetch(props.fileUrl)
      .then((r) => {
        r.text().then((txt) => {
          const txtBlob = new Blob([txt], {
            type: "text/plain",
          });
          const objectURL = txt;
          setTxtObjectURL(objectURL);
        });
      })
      .catch((_) => {
        setError(true);
      });
  }

  function loadMarkdownIntoMemory() {
    fetch(props.fileUrl)
      .then((response) => response.text())
      .then((text) => {
        if (!isCancelled) {
          setMarkdownContent(text);
        }
      })
      .catch((_) => {
        if (!isCancelled) {
          setError(true);
        }
      });
  }

  function loadSVGIntoMemory() {
    fetch(props.fileUrl)
      .then((r) => r.text())
      .then((svgText) => {
        if (isCancelled) return;
        const svgBlob = new Blob([svgText], { type: "image/svg+xml" });
        const objectURL = URL.createObjectURL(svgBlob);
        setSvgObjectURL(objectURL);
      })
      .catch((_) => !isCancelled && setError(true));
  }

  function parseCSV(text: string): string[][] {
    const lines = text.split(/\r?\n/);
    const result: string[][] = [];

    for (const line of lines) {
      if (!line.trim()) continue; // Skip empty lines

      const row: string[] = [];
      let current = "";
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            // Escaped quote
            current += '"';
            i++;
          } else {
            // Toggle quote mode
            inQuotes = !inQuotes;
          }
        } else if (char === "," && !inQuotes) {
          // End of field
          row.push(current);
          current = "";
        } else {
          current += char;
        }
      }

      // Add the last field
      row.push(current);
      result.push(row);
    }

    return result;
  }

  function loadCSVIntoMemory() {
    fetch(props.fileUrl)
      .then((response) => response.text())
      .then((text) => {
        if (!isCancelled) {
          const parsed = parseCSV(text);
          setCsvData(parsed);
        }
      })
      .catch((_) => {
        if (!isCancelled) {
          setError(true);
        }
      });
  }

  function renderFileViewer() {
    if (props.fileType === "pdf" && !props.isPreviewModal) {
      return (
        <div
          style={{
            width: "100%",
            height: "117px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {imagePreview !== "" ? (
            <img src={imagePreview as string} style={{ width: "100%", height: "100%" }} />
          ) : (
            <Spinner
              variant={"indeterminate"}
              sx={{
                color: Colors.LAZO_PURPLE,
              }}
            />
          )}
        </div>
      );
    } else if (props.fileType === "pdf") {
      return pdfObjectURL !== "" ? (
        <object
          data={pdfObjectURL}
          // Revoke to prevent memory leaks
          onLoad={() => URL.revokeObjectURL(pdfObjectURL)}
          type="application/pdf"
          style={{
            width: props.viewportWidth ?? "233px",
            height: props.viewportHeight ?? "117px",
            border: "none",
          }}
        />
      ) : (
        <Spinner
          variant={"indeterminate"}
          sx={{
            color: Colors.LAZO_PURPLE,
          }}
        />
      );
    } else if (props.fileType === "svg") {
      return svgObjectURL !== "" ? (
        <img
          src={svgObjectURL}
          onLoad={() => URL.revokeObjectURL(svgObjectURL)}
          style={{
            width: "auto",
            height: props.viewportHeight ?? "117px",
            maxWidth: props.viewportWidth ?? "233px",
          }}
          alt="SVG Preview"
        />
      ) : (
        <Spinner
          variant={"indeterminate"}
          sx={{
            color: Colors.LAZO_PURPLE,
          }}
        />
      );
    } else if (props.fileType === "md") {
      return markdownContent !== "" ? (
        <Box
          style={{
            width: props.viewportWidth ?? "233px",
            height: props.viewportHeight ?? "117px",
            border: `1px solid ${Colors.LIGHT_GRAY2}`,
            borderRadius: "5px",
            padding: "1rem",
            overflowY: "auto",
            background: "white",
            fontFamily: "sans-serif",
            fontSize: "14px",
            lineHeight: 1.5,
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdownContent}</ReactMarkdown>
        </Box>
      ) : (
        <Spinner
          variant={"indeterminate"}
          sx={{
            color: Colors.LAZO_PURPLE,
          }}
        />
      );
    } else if (props.fileType === "csv") {
      return csvData.length > 0 ? (
        <Box
          style={{
            width: props.viewportWidth ?? "233px",
            height: props.viewportHeight ?? "117px",
            border: `1px solid ${Colors.LIGHT_GRAY2}`,
            borderRadius: "5px",
            overflowX: "auto",
            overflowY: "auto",
            background: "white",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "12px",
            }}
          >
            <thead>
              <tr>
                {csvData[0]?.map((header, index) => (
                  <th
                    key={index}
                    style={{
                      padding: "8px",
                      borderBottom: `2px solid ${Colors.LIGHT_GRAY2}`,
                      backgroundColor: Colors.LIGHT_GRAY2,
                      fontWeight: 600,
                      textAlign: "left",
                      position: "sticky",
                      top: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {csvData.slice(1).map((row, rowIndex) => (
                <tr key={rowIndex} style={{ borderBottom: `1px solid ${Colors.LIGHT_GRAY2}` }}>
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      style={{
                        padding: "8px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      ) : (
        <Spinner
          variant={"indeterminate"}
          sx={{
            color: Colors.LAZO_PURPLE,
          }}
        />
      );
    } else if (filesTypes.includes(props.fileType)) {
      if (props.fileType === "txt") {
        return txtObjectURL !== "" ? (
          <Box
            style={{
              width: props.viewportWidth ?? "233px",
              height: props.viewportHeight ?? "117px",
              border: `1px solid ${Colors.LIGHT_GRAY2}`,
              borderRadius: "5px",
            }}
          >
            <Typography variant="body1" sx={{ padding: "2rem" }}>
              {txtObjectURL}
            </Typography>
          </Box>
        ) : (
          <Spinner
            variant={"indeterminate"}
            sx={{
              color: Colors.LAZO_PURPLE,
            }}
          />
        );
      } else {
        const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(props.fileUrl)}`;
        return (
          <iframe
            src={viewerUrl}
            style={{
              width: props.viewportWidth ?? "233px",
              height: props.viewportHeight ?? "117px",
              border: "none",
            }}
            title="Document Preview"
          />
        );
        //temporaly disabled
        // return (
        //     <div style={{ width: '233px', height: '117px',
        //                        justifyContent:"center",
        //     alignItems:"center"
        //      }}>
        //              {/* <img src={imagePreview} alt="Document Preview" style={{ width: '100%', height: '100%' }} /> */}
        //             <DocViewer
        //             documents={[{ uri: props.fileUrl },]}
        //             config={{
        //                 header: {
        //                     disableHeader: true,
        //                     disableFileName: true,
        //                     retainURLParams: false,
        //                 },
        //             }}
        //             style={{ width: '233px', height: '117px',
        //                 display:"flex",
        //                 justifyContent:"center",
        //                 alignItems:"center"
        //              }}
        //             prefetchMethod="GET"
        //             requestHeaders={{
        //                 'Access-Control-Allow-Origin': '*',
        //                 'Access-Control-Allow-Methods': 'GET',
        //                 'Access-Control-Allow-Headers': '*',
        //             }
        //         }
        //         />
        //     </div>
        // );
      }
    } else if (props.fileType === "image") {
      return !props.fileUrl ? <Spinner style={{ color: Colors.LAZO_PURPLE }} size={"2rem"} /> : <img src={props.fileUrl} />;
    } else {
      return <p>No preview available for this file type</p>;
    }
  }

  return (
    <div
      style={{
        width: "100%",
        maxWidth: props.viewportWidth ?? "auto",
        maxHeight: props.viewportHeight ?? "auto",
        overflowY: "hidden",
      }}
    >
      {renderFileViewer()}
    </div>
  );
}
